const YEAR_PATTERN = /\b(19|20)\d{2}\b/;

const MODEL_NAME_STOP_WORDS = new Set([
  'adult',
  'adults',
  'adolescent',
  'adolescents',
  'bayesian',
  'child',
  'children',
  'evaluation',
  'elderly',
  'infant',
  'infants',
  'lifespan',
  'model',
  'models',
  'neonate',
  'neonates',
  'nicu',
  'pediatric',
  'pediatrics',
  'population',
  'posthoc',
  'preterm',
  'relationship',
  'term',
  'validation',
]);

const slugify = (value?: string | number | null): string => {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return '';
  return text
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const extractYear = (...sources: Array<string | number | null | undefined>): string => {
  for (const source of sources) {
    const text = String(source ?? '').trim();
    if (!text) continue;
    const matched = text.match(YEAR_PATTERN);
    if (matched?.[0]) return matched[0];
  }
  return '';
};

const extractAuthorSlug = (...sources: Array<string | null | undefined>): string => {
  for (const source of sources) {
    const text = String(source ?? '').replace(/\(.*?\)/g, ' ').trim();
    if (!text) continue;
    const normalized = slugify(text);
    if (!normalized) continue;
    const [firstSegment] = normalized.split('_').filter(Boolean);
    if (firstSegment) return firstSegment;
  }
  return '';
};

const inferDrugSlugFromModelId = (modelId?: string | null, preferredDrugName?: string | null): string => {
  const preferred = slugify(preferredDrugName);
  if (preferred) return preferred;

  const tokens = slugify(modelId).split('_').filter(Boolean);
  if (!tokens.length) return '';

  const yearIndex = tokens.findIndex((token) => YEAR_PATTERN.test(token));
  if (yearIndex >= 0) {
    const afterYear = tokens.slice(yearIndex + 1).filter((token) => !MODEL_NAME_STOP_WORDS.has(token));
    if (afterYear.length) return afterYear[0];

    const beforeYear = tokens.slice(0, yearIndex).filter((token) => !MODEL_NAME_STOP_WORDS.has(token));
    if (beforeYear.length >= 2) return beforeYear[0];
  }

  const afterLeadToken = tokens.slice(1).find((token) => !MODEL_NAME_STOP_WORDS.has(token) && !YEAR_PATTERN.test(token));
  if (afterLeadToken) return afterLeadToken;

  return tokens.find((token) => !MODEL_NAME_STOP_WORDS.has(token) && !YEAR_PATTERN.test(token)) || '';
};

export interface ModelDisplayNameInput {
  modelId?: string | null;
  modelName?: string | null;
  displayName?: string | null;
  drugName?: string | null;
  firstAuthor?: string | null;
  year?: string | number | null;
  publicationInfo?: {
    first_author?: string | null;
    year?: string | number | null;
  } | null;
}

export const getCanonicalModelDisplayName = (input: ModelDisplayNameInput): string => {
  const explicit = slugify(input.displayName);
  if (explicit) return explicit;

  const year = extractYear(input.year, input.publicationInfo?.year, input.modelName, input.modelId);
  const author = extractAuthorSlug(input.firstAuthor, input.publicationInfo?.first_author, input.modelName);
  const drug = inferDrugSlugFromModelId(input.modelId, input.drugName);
  const orderedParts = [drug, author, year].filter(Boolean);
  const uniqueParts = orderedParts.filter((part, index) => orderedParts.indexOf(part) === index);

  if (uniqueParts.length >= 2) {
    return uniqueParts.join('_');
  }

  return slugify(input.modelId) || slugify(input.modelName) || 'unknown_model';
};
